import axios from "../../utils/axios.tsx";
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import TallyButton from "../common/TallyButton.tsx";
import ProgressMessage from "./ProgressMessage.tsx";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { useDispatch, useSelector } from "react-redux";
import { checkEconomicIntegration } from "../../store/economic";
import { checkIntempusIntegration } from "../../store/intempus";
import { checkFenerumIntegration } from "../../store/fenerum";
import { RootState, AppDispatch } from "../../store/types";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import { useSSE } from "../../hooks";
import { useLocalizedSyncMessage } from "../../hooks/useLocalizedSyncMessage";
import { BASE_URL } from "../../const.ts";
import type {
  SyncStatus,
  SyncIntegrationResponse,
  ApiResponse,
  SSEMessage,
  SSEOptions,
} from "../../types";
import { pushAnalyticsEvent } from "../../utils/googleEvent.ts";
import { streamAsk } from "../search/streamAsk.tsx";
import IntempusConnectDialog from "./IntempusConnectDialog.tsx";

interface SyncStatusProps {
  children?: React.ReactNode;
  role: string;
  content?: string;
  onPulseAnimation?: () => void;
  stopPulseAnimation?: () => void;
}

// Utility function to convert UTC to local time
const formatDate = (utcString: string) => {
  if (!utcString) return "N/A";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(utcString));
};

// Component for rendering individual sync messages
const SyncMessageItem: React.FC<{
  message: string | Record<string, any>;
  index: number;
  isLast: boolean;
}> = ({ message, index, isLast }) => {
  let displayMessage: string;

  // Check if message is a LocalizedMessage object
  if (typeof message === "object" && message !== null && "key" in message) {
    // It's a LocalizedMessage object - use the hook
    const localizedMsg = useLocalizedSyncMessage(message as any);
    displayMessage = (localizedMsg || message.key || "") as string;
  } else if (typeof message === "string") {
    // It's a plain string
    displayMessage = message;
  } else {
    displayMessage = String(message);
  }

  return (
    <ProgressMessage
      progressUpdate={isLast}
      sx={{ marginBottom: "8px", display: "block" }}
    >
      {index > 0 && "✓ "}
      {displayMessage}
    </ProgressMessage>
  );
};

function syncIntegration(
  integration_identifier: string = "economic",
): Promise<ApiResponse<SyncIntegrationResponse>> {
  return axios.post(
    "/v1/integrations/sync/",
    JSON.stringify({ integration_identifier: integration_identifier }),
    { withCredentials: true },
  );
}

// Fetch sync status for a specific integration
async function fetchIntegrationSyncStatus(
  integration_identifier: string,
): Promise<SyncStatus | null> {
  const response = await axios.get<{ objects: SyncStatus[]; type: string }>(
    `/v1/integrations/sync_status/?integration=${integration_identifier}`,
  );
  return response.data.objects[0] || null;
}

// Integration display names
const INTEGRATION_DISPLAY_NAMES: Record<string, string> = {
  economic: "e-conomic",
  intempus: "Intempus",
  fenerum: "Fenerum",
};

// Sub-component for displaying a single integration's sync status
interface IntegrationSectionProps {
  integrationIdentifier: string;
  onPulseAnimation?: () => void;
  stopPulseAnimation?: () => void;
  onSyncStatusChange?: (status: SyncStatus) => void;
  onSyncCompleted?: () => void;
  shouldAutoSync?: boolean;
}

const IntegrationSection: React.FC<IntegrationSectionProps> = ({
  integrationIdentifier,
  onPulseAnimation,
  stopPulseAnimation,
  onSyncStatusChange,
  onSyncCompleted,
  shouldAutoSync = false,
}) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isLoadingSyncStatus, setIsLoadingSyncStatus] = useState(true);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [awaitingSyncResponse, setAwaitingSyncResponse] = useState(false);
  const [syncMessages, setSyncMessages] = useState<
    (string | Record<string, any>)[]
  >([]);
  const [sseUrl, setSseUrl] = useState<string | null>(null);
  const hasAutoSynced = useRef(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const additionalInfoRef = useRef<HTMLDivElement>(null);

  // Fetch sync status for this integration on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setFetchError(false);
        const status = await fetchIntegrationSyncStatus(integrationIdentifier);
        if (status) {
          setSyncStatus(status);
        } else {
          // No sync status found - set a default
          setSyncStatus({
            sync_id: null,
            last_sync_start: null,
            last_sync_end: null,
            last_successful_sync_start: null,
            last_successful_sync_end: null,
            last_error_traceback: "",
            integration: integrationIdentifier,
          });
        }
      } catch (error) {
        console.error(
          `Failed to fetch sync status for ${integrationIdentifier}:`,
          error,
        );
        setFetchError(true);
      } finally {
        setIsLoadingSyncStatus(false);
      }
    };
    fetchStatus();
  }, [integrationIdentifier]);

  // Notify parent when sync status changes
  useEffect(() => {
    if (syncStatus && onSyncStatusChange) {
      onSyncStatusChange(syncStatus);
    }
  }, [syncStatus, onSyncStatusChange]);

  // Check if additional_information content overflows (needs "Show more")
  useEffect(() => {
    const checkOverflow = () => {
      if (additionalInfoRef.current) {
        const element = additionalInfoRef.current;
        setHasOverflow(element.scrollHeight > element.clientHeight);
      }
    };
    // Check after render and when syncStatus changes
    checkOverflow();
    // Also check on window resize
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [syncStatus?.additional_information, isExpanded]);

  // SSE options for this integration
  const sseOptions = useMemo<SSEOptions>(
    () => ({
      onMessage: (data: SSEMessage) => {
        const timestamp = new Date().toISOString();
        console.log(
          `[${timestamp}] ${integrationIdentifier} received SSE message:`,
          data,
        );
        if (data.type === "status") {
          setSyncStatus(data.data);
          const messageText = data.data.message || data.data.body;
          if (messageText) {
            setSyncMessages((prev) => [...prev, messageText]);
          }
        } else if (data.type === "completed" || data.type === "stream_ended") {
          console.log(
            `[${timestamp}] Sync completed for ${integrationIdentifier}`,
          );
          // Re-fetch sync status from API to update the UI with final state
          fetchIntegrationSyncStatus(integrationIdentifier)
            .then((status) => {
              if (status) {
                setSyncStatus(status);
              }
            })
            .catch((err) =>
              console.error(
                `Failed to re-fetch sync status for ${integrationIdentifier}:`,
                err,
              ),
            );
          setSseUrl(null);
          setAwaitingSyncResponse(false);
          if (stopPulseAnimation) {
            stopPulseAnimation();
          }
          setSyncMessages([]);
          if (onSyncCompleted) {
            onSyncCompleted();
          }
        } else if (data.type === "error") {
          console.error(
            `[${timestamp}] SSE error for ${integrationIdentifier}:`,
            data.message,
          );
          setSseUrl(null);
          setAwaitingSyncResponse(false);
          if (stopPulseAnimation) {
            stopPulseAnimation();
          }
          setSyncMessages([]);
        }
      },
      onError: (error: Event) => {
        console.error(
          `SSE connection error for ${integrationIdentifier}:`,
          error,
        );
        setSseUrl(null);
        setAwaitingSyncResponse(false);
        if (stopPulseAnimation) {
          stopPulseAnimation();
        }
        setSyncMessages([]);
      },
      onOpen: () =>
        console.log(`SSE connection opened for ${integrationIdentifier}`),
      onClose: () =>
        console.log(`SSE connection closed for ${integrationIdentifier}`),
    }),
    [integrationIdentifier, stopPulseAnimation, onSyncCompleted],
  );

  const { closeConnection } = useSSE(sseUrl, sseOptions);

  useEffect(() => {
    return () => closeConnection();
  }, [closeConnection]);

  const syncNow = useCallback(async () => {
    setAwaitingSyncResponse(true);
    setSyncMessages([]);
    if (onPulseAnimation) {
      onPulseAnimation();
    }

    try {
      const syncResponse = await syncIntegration(integrationIdentifier);
      const signedValue = syncResponse.data.signed_value;
      const syncId = syncResponse.data.sync_id;

      if (!signedValue || !syncId) {
        console.error(
          `Missing signed_value or sync_id for ${integrationIdentifier}`,
        );
        setAwaitingSyncResponse(false);
        if (stopPulseAnimation) {
          stopPulseAnimation();
        }
        return;
      }

      setSseUrl(
        `${BASE_URL}/v1/integrations/sync/stream/${integrationIdentifier}/?user_signature=${encodeURIComponent(signedValue)}&sync_id=${encodeURIComponent(syncId)}`,
      );
    } catch (error) {
      console.error(`Error syncing ${integrationIdentifier}:`, error);
      setAwaitingSyncResponse(false);
      if (stopPulseAnimation) {
        stopPulseAnimation();
      }
    }
  }, [integrationIdentifier, onPulseAnimation, stopPulseAnimation]);

  // Auto-sync on mount if requested (for first sync after integration connect)
  useEffect(() => {
    if (
      shouldAutoSync &&
      !hasAutoSynced.current &&
      !awaitingSyncResponse &&
      !isLoadingSyncStatus
    ) {
      hasAutoSynced.current = true;
      syncNow();
    }
  }, [shouldAutoSync, awaitingSyncResponse, isLoadingSyncStatus, syncNow]);

  if (isLoadingSyncStatus) {
    return (
      <Box sx={{ marginBottom: "20px" }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          color="black"
          sx={{ marginBottom: "8px" }}
        >
          {INTEGRATION_DISPLAY_NAMES[integrationIdentifier] ||
            integrationIdentifier}
        </Typography>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ marginBottom: "20px" }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          color="black"
          sx={{ marginBottom: "8px" }}
        >
          {INTEGRATION_DISPLAY_NAMES[integrationIdentifier] ||
            integrationIdentifier}
        </Typography>
        <Typography variant="body1" color="error">
          <Trans context="Error message when sync status fetch fails">
            We were unable to fetch the sync status right now. Please try again
            later.
          </Trans>
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ marginTop: "8px" }}
        >
          <Trans context="Suggestion to check status page for system issues">
            You can also check status.asktally.com to see if there are problems
            with the system.
          </Trans>
        </Typography>
      </Box>
    );
  }

  if (!syncStatus) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: "20px" }}>
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        color="black"
        sx={{ marginBottom: "8px" }}
      >
        {INTEGRATION_DISPLAY_NAMES[integrationIdentifier] ||
          integrationIdentifier}
      </Typography>
      {awaitingSyncResponse ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            alignItems: "flex-start",
            minHeight: "100px",
            paddingTop: "10px",
          }}
        >
          {syncMessages.length > 0 ? (
            <Box sx={{ textAlign: "left", maxWidth: "100%" }}>
              {syncMessages.map((message, index) => (
                <SyncMessageItem
                  key={index}
                  message={message}
                  index={index}
                  isLast={index === syncMessages.length - 1}
                />
              ))}
            </Box>
          ) : (
            <CircularProgress />
          )}
        </Box>
      ) : (
        <>
          {syncStatus.last_error_traceback ? (
            <>
              <Typography variant="body1" color="black">
                {`${i18n._(
                  msg({
                    context:
                      "Response title which is followed by the last sync status info",
                    message: "Last sync:",
                  }),
                )} ${formatDate(syncStatus.last_sync_end || "")}`}
              </Typography>
              <Typography variant="body1" color="black">
                {`${i18n._(
                  msg({
                    context:
                      "Response title which is followed by the error while trying to fetch the last sync status info",
                    message: "Error:",
                  }),
                )} ${syncStatus.last_error_traceback}`}
                <br />
                <Trans context="Explanation of the next steps taken after there is an error with sync.">
                  An engineer will look into this within the next 4 hours within
                  working hours.
                </Trans>
              </Typography>
              <Typography variant="body1" color="black">
                {`${i18n._(
                  msg({
                    context:
                      "Response title which is followed by the last successful sync status info",
                    message: "Last successful sync:",
                  }),
                )} ${formatDate(syncStatus.last_successful_sync_end || "")}`}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" color="black">
                {`${i18n._(
                  msg({
                    context:
                      "Response title which is followed by the last successful sync status info",
                    message: "Last successful sync:",
                  }),
                )} ${formatDate(syncStatus.last_successful_sync_end || "")}`}
              </Typography>
              {syncStatus.additional_information && (
                <>
                  <Box
                    ref={additionalInfoRef}
                    sx={{
                      ...(!isExpanded && {
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }),
                    }}
                  >
                    <Typography variant="body1" color="black" component="div">
                      <CustomReactMarkDown
                        content={syncStatus.additional_information}
                      />
                    </Typography>
                  </Box>
                  {(hasOverflow || isExpanded) && (
                    <Typography
                      variant="body2"
                      component="span"
                      onClick={() => setIsExpanded(!isExpanded)}
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        textDecoration: "underline",
                        marginTop: "4px",
                        display: "inline-block",
                        "&:hover": {
                          opacity: 0.8,
                        },
                      }}
                    >
                      {isExpanded ? (
                        <Trans context="Button to collapse expanded content">
                          Show less
                        </Trans>
                      ) : (
                        <Trans context="Button to expand truncated content">
                          Show more
                        </Trans>
                      )}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
      {!awaitingSyncResponse && (
        <TallyButton
          sx={{ marginBottom: "2%", marginTop: "2%" }}
          onClick={syncNow}
        >
          <Trans context="Button title enabling the user to sync the status.">
            Sync Now
          </Trans>
        </TallyButton>
      )}
    </Box>
  );
};

const SyncStatus: React.FC<SyncStatusProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    hasIntegration: hasEconomicIntegration,
    isLoading: isLoadingEconomic,
  } = useSelector((state: RootState) => state.economic);
  const {
    hasIntegration: hasIntempusIntegration,
    isLoading: isLoadingIntempus,
  } = useSelector((state: RootState) => state.intempus);
  const { hasIntegration: hasFenerumIntegration, isLoading: isLoadingFenerum } =
    useSelector((state: RootState) => state.fenerum);
  const isAuthenticated = useSelector(
    (state: RootState) => state.authState.isLoggedIn,
  );
  const selectedModel = useSelector(
    (state: RootState) => state.conversationState.selectedModel,
  );
  const [shouldAutoSyncEconomic, setShouldAutoSyncEconomic] = useState(false);
  const hasTriggeredCustomGoalRef = useRef(false);
  const [shouldAutoSyncIntempus, setShouldAutoSyncIntempus] = useState(false);
  const [isIntempusDialogOpen, setIsIntempusDialogOpen] = useState(false);
  const stopStreamRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (isLoadingEconomic) {
      dispatch(checkEconomicIntegration());
    }
    if (isLoadingIntempus) {
      dispatch(checkIntempusIntegration());
    }
    if (isLoadingFenerum) {
      dispatch(checkFenerumIntegration());
    }
  }, [dispatch, isLoadingEconomic, isLoadingIntempus, isLoadingFenerum]);

  // Check if we should start the first sync after connecting
  useEffect(() => {
    const shouldStartFirstSync = localStorage.getItem("shouldStartFirstSync");
    if (shouldStartFirstSync === "true" && hasEconomicIntegration) {
      localStorage.removeItem("shouldStartFirstSync");
      setShouldAutoSyncEconomic(true);
    }
  }, [hasEconomicIntegration]);

  // Trigger custom goal after first sync completes during signup
  const triggerPostSyncWelcome = useCallback(async () => {
    if (hasTriggeredCustomGoalRef.current) {
      return;
    }

    const welcomeConversationId = localStorage.getItem(
      "welcome_conversation_identifier",
    );
    if (!welcomeConversationId) {
      return;
    }

    hasTriggeredCustomGoalRef.current = true;

    const stopAnimation = () => {};
    const exchangeParams = {
      conversation_id: welcomeConversationId,
      prompt: "",
      custom_goal_identifier: "post_first_sync_welcome",
      model: selectedModel,
    };
    try {
      await streamAsk({
        dispatch,
        isAuthenticated,
        stopAnimation,
        exchangeURL: "/v1/exchange/",
        streamSlug: "/v1/stream_ask/",
        exchangeParams: exchangeParams,
        stopStreamRef,
      });
    } catch (err) {
      console.error("Failed to trigger post_first_sync_welcome:", err);
    }
    localStorage.removeItem("welcome_conversation_identifier");
  }, [dispatch, isAuthenticated, selectedModel, stopStreamRef]);

  const getEconomicAuthUrl = () => {
    return import.meta.env.VITE_TAILSCALE_FUNNEL_BASE_URL
      ? `https://secure.e-conomic.com/secure/api1/requestaccess.aspx?appPublicToken=zDuGmuztIyDCRvPmk5heLjPY8rfbYxlc6Tqf1p2MEbU1&redirectUrl=${encodeURIComponent(
          `${import.meta.env.VITE_TAILSCALE_FUNNEL_BASE_URL}/integration/e-conomic/connect`,
        )}`
      : "https://secure.e-conomic.com/secure/api1/requestaccess.aspx?appPublicToken=2W6NhwKKvweaPWY19vxXs0mn1GtiVk49xIYOq3R2z2o&redirectUrl=https%3A%2F%2Fasktally.com%2Fintegration%2Fe-conomic%2Fconnect";
  };

  const handleConnectEconomic = () => {
    pushAnalyticsEvent("integration_connect_started", {
      step_number: 7,
      integration: "e-conomic",
    });

    // 2. Redirect user
    window.open(getEconomicAuthUrl(), "_blank", "noopener,noreferrer");
  };

  const handleOpenIntempusDialog = () => {
    pushAnalyticsEvent("integration_connect_started", {
      step_number: 7,
      integration: "intempus",
    });
    setIsIntempusDialogOpen(true);
  };

  const handleIntempusConnectSuccess = () => {
    setIsIntempusDialogOpen(false);
    // Refresh integration status
    dispatch(checkIntempusIntegration());
    // Trigger auto-sync after successful connection
    setShouldAutoSyncIntempus(true);
  };

  // Parse content prop to check for welcome message
  let parsedSyncStatus: SyncStatus | null = null;
  if (props.content) {
    try {
      parsedSyncStatus = JSON.parse(props.content);
    } catch (e) {
      console.error("Failed to parse SyncStatus content:", e);
    }
  }

  return (
    <Box marginBottom={"4%"}>
      <Grid container>
        <Grid size={{ xs: 1, md: 1, lg: 1 }}>
          <AvatarPanel />
        </Grid>
        <Grid size={{ xs: 12, md: 10, lg: 10 }}>
          {parsedSyncStatus?.title_message && (
            <Box sx={{ marginBottom: "3%" }}>
              <CustomReactMarkDown content={parsedSyncStatus.title_message} />
            </Box>
          )}
          <Box sx={{ marginBottom: "3%" }}>
            <CustomReactMarkDown
              content={
                i18n._({ id: "integration.status.title" }) ||
                "Integration Status"
              }
            />
          </Box>
          {hasEconomicIntegration && (
            <IntegrationSection
              integrationIdentifier="economic"
              onPulseAnimation={props.onPulseAnimation}
              stopPulseAnimation={props.stopPulseAnimation}
              onSyncCompleted={triggerPostSyncWelcome}
              shouldAutoSync={shouldAutoSyncEconomic}
            />
          )}
          {hasIntempusIntegration ? (
            <IntegrationSection
              integrationIdentifier="intempus"
              onPulseAnimation={props.onPulseAnimation}
              stopPulseAnimation={props.stopPulseAnimation}
              shouldAutoSync={shouldAutoSyncIntempus}
            />
          ) : (
            !isLoadingIntempus && (
              <Box sx={{ marginBottom: "20px" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="black"
                  sx={{ marginBottom: "8px" }}
                >
                  Intempus
                </Typography>
                <TallyButton onClick={handleOpenIntempusDialog}>
                  <Trans context="Button title enabling the user to connect to Intempus service.">
                    Click here to connect to Intempus
                  </Trans>
                </TallyButton>
              </Box>
            )
          )}
          {hasFenerumIntegration && (
            <IntegrationSection
              integrationIdentifier="fenerum"
              onPulseAnimation={props.onPulseAnimation}
              stopPulseAnimation={props.stopPulseAnimation}
            />
          )}
          {!isLoadingEconomic && !hasEconomicIntegration && (
            <Box sx={{ marginBottom: "20px" }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color="black"
                sx={{ marginBottom: "8px" }}
              >
                e-conomic
              </Typography>
              <TallyButton onClick={handleConnectEconomic}>
                <Trans
                  context={
                    "Button title enabling the user to connect to 'e-conomic' service."
                  }
                >
                  Click here to connect to e-conomic
                </Trans>
              </TallyButton>
            </Box>
          )}
        </Grid>
      </Grid>
      <IntempusConnectDialog
        open={isIntempusDialogOpen}
        onClose={() => setIsIntempusDialogOpen(false)}
        onSuccess={handleIntempusConnectSuccess}
      />
    </Box>
  );
};

export default SyncStatus;
